import React from 'react';
import { ArrowLeft, Flame, Mail } from 'lucide-react';
import { Button } from './ui/Button';
import { ThemeToggle } from './ui/ThemeToggle';
import PublicContactForm from './support/PublicContactForm';
import { ContactResponse } from '../types';

interface ContactSupportPageProps {
  onBack?: () => void;
}

export const ContactSupportPage: React.FC<ContactSupportPageProps> = ({ onBack }) => {
  const handleSuccess = (response: ContactResponse) => {
    // Optional: could auto-redirect back after success
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {onBack && (
              <button
                onClick={onBack}
                className="flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5 mr-1" />
                <span className="text-sm font-medium">Back</span>
              </button>
            )}
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <Flame className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold text-gray-900 dark:text-white">Koru</span>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title */}
        <div className="mb-10">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center">
              <Mail className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">Contact Support</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            Have a question, found a bug, or need help with something? Fill out the form below and we'll get back to you as soon as possible.
          </p>
        </div>

        {/* Contact Form */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          <PublicContactForm
            onSuccess={handleSuccess}
            onCancel={onBack}
          />
        </div>

        {onBack && (
          <div className="mt-8 text-center">
            <Button onClick={onBack} variant="outline" leftIcon={<ArrowLeft size={16} />}>
              Back to Koru
            </Button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} Koru. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
